import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  updateDoc,
  where,
  limit,
  setDoc,
  writeBatch,
} from 'firebase/firestore/lite';
import { NotificationData } from 'types/notification';
import { firestore } from 'utils/firebase';

export type NewNotificationData = Omit<NotificationData, 'id'>;

export default class NotificationApi {
  private static readonly collectionReference = collection(firestore, 'notifications');

  static async addNotification(newNotification: NewNotificationData) {
    const docRef = doc(this.collectionReference);
    await setDoc(docRef, { ...newNotification, id: docRef.id } as NotificationData);
    return docRef.id;
  }

  public static async getNotification(id: string): Promise<NotificationData> {
    const docRef = doc(this.collectionReference, id);
    const docSnap = await getDoc(docRef);
    //@ts-ignore
    return docSnap.data()!;
  }

  public static async getNotificationsForUser(uuid: string): Promise<NotificationData[]> {
    const querySnap = await getDocs(query(this.collectionReference, where('userId', '==', uuid)));
    return querySnap.docs.map(docSnap => docSnap.data() as NotificationData);
  }

  /**
   * Retrieves notifications for a user.
   *
   * @param {string} targetUserId - The ID of the user to retrieve notifications for.
   * @param {NotificationData | null} lastNotification - The last notification the user received, if any.
   * @returns {Promise<NotificationData[] | null>} A promise that resolves to an array of notification data or null if there are no new notifications.
   * @throws {Error} If there is an error retrieving the notifications.
   */
  public static async getNotificationsByTargetUserId(
    targetUserId: string,
    lastNotification: NotificationData | null
  ): Promise<NotificationData[] | null> {
    const querySnapsTest = await getDocs(
      query(this.collectionReference, where('targetUserId', '==', targetUserId), orderBy('created', 'desc'), limit(1))
    );

    if (querySnapsTest.docs.length > 0) {
      if (lastNotification?.id === querySnapsTest.docs[0].data().id) {
        return null;
      }
    }

    const querySnaps = await getDocs(
      query(this.collectionReference, where('targetUserId', '==', targetUserId), orderBy('created', 'asc'))
    );

    return querySnaps.docs.map(docSnap => docSnap.data() as NotificationData);
  }

  public static async setNotificationToRead(notificationId: string) {
    return updateDoc(doc(this.collectionReference, notificationId), { read: true });
  }

  public static async setAllNotificationsToRead(notifications: NotificationData[]) {
    const batch = writeBatch(firestore);

    notifications.forEach(notification => {
      const docRef = doc(this.collectionReference, notification.id);
      batch.update(docRef, { ...notification, read: true });
    });

    await batch.commit();
  }
  static async updateNotification(notification: NotificationData) {
    const docRef = doc(this.collectionReference, notification.id);
    await updateDoc(docRef, {
      ...notification,
    });
    return docRef.id;
  }
}
